/**
 * Утилиты для работы с сообщениями в чате.
 * XSS защита, форматирование, валидация.
 */

// Максимальная длина сообщения
export const MAX_MESSAGE_LENGTH = 4000;

// Паттерны для ссылок
const URL_PATTERN = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;

/**
 * Экранирует HTML символы для защиты от XSS.
 */
export function escapeHtml(text: string): string {
  if (!text) return '';
  
  const htmlEntities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return text.replace(/[&<>"']/g, (char) => htmlEntities[char] || char);
}

/**
 * Санитизация текста сообщения перед отправкой.
 */
export function sanitizeMessage(text: string): string {
  if (!text) return '';
  
  // Обрезаем по длине
  let sanitized = text.slice(0, MAX_MESSAGE_LENGTH);
  
  // Удаляем null bytes и control characters
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
  
  // Нормализуем переносы строк
  const lines = sanitized.split('\n');
  if (lines.length > 50) {
    sanitized = lines.slice(0, 50).join('\n') + '\n...';
  }
  
  // Убираем лишние пробелы
  sanitized = sanitized.trim();
  
  return sanitized;
}

/**
 * Валидация сообщения перед отправкой.
 */
export function validateMessage(text: string): { isValid: boolean; error?: string } {
  if (!text || !text.trim()) {
    return { isValid: false, error: 'Сообщение не может быть пустым' };
  }
  
  if (text.length > MAX_MESSAGE_LENGTH) {
    return { isValid: false, error: `Сообщение слишком длинное (максимум ${MAX_MESSAGE_LENGTH} символов)` };
  }
  
  // Проверка на спам (повторяющиеся символы)
  for (const char of text) {
    if (char.repeat(15).length <= text.split(char.repeat(15)).length - 1) {
      if (char !== ' ' && char !== '\n') {
        return { isValid: false, error: 'Обнаружен спам (повторяющиеся символы)' };
      }
    }
  }
  
  return { isValid: true };
}

/**
 * Форматирует текст сообщения для отображения.
 * Конвертирует URL в кликабельные ссылки, экранирует HTML.
 */
export function formatMessageText(text: string, opts?: { linkify?: boolean }): string {
  // Защита от undefined/null
  if (!text) return '';
  
  // Экранируем HTML
  let formatted = escapeHtml(text);
  
  // Конвертируем URL в ссылки (опционально).
  // Комментарий на русском: выключение "превью ссылок" ускоряет рендер и убирает лишние интерактивные элементы.
  if (opts?.linkify !== false) {
    formatted = formatted.replace(URL_PATTERN, (url) => {
      const safeUrl = escapeHtml(url);
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer external" class="text-blue-400 hover:underline break-all">${safeUrl}</a>`;
    });
  }
  
  // Конвертируем переносы строк в <br>
  formatted = formatted.replace(/\n/g, '<br>');
  
  return formatted;
}

/**
 * Возвращает первую http/https ссылку в тексте (для предпросмотра).
 */
export function extractFirstUrl(text: string): string | null {
  if (!text) return null;
  const m = text.match(URL_PATTERN);
  return m && m.length ? m[0] : null;
}

/**
 * Форматирует время сообщения.
 */
export function formatMessageTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    // Сегодня - показываем время
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Вчера';
  } else if (diffDays < 7) {
    // На этой неделе - показываем день недели
    return date.toLocaleDateString('ru-RU', { weekday: 'short' });
  } else {
    // Давно - показываем дату
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  }
}

/**
 * Форматирует полную дату для тултипа.
 */
export function formatFullDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Debounce функция для typing индикатора.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Throttle функция для ограничения частоты вызовов.
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Статусы сообщения.
 * pending — ожидает отправки (офлайн-режим).
 */
export type MessageStatus = 'pending' | 'sending' | 'sent' | 'delivered' | 'read' | 'error';

/**
 * Получает иконку статуса сообщения.
 */
export function getMessageStatusIcon(status: MessageStatus): string {
  // Комментарий на русском: UX мессенджера.
  // - не доставлено (pending/sending) — без галочек (пузырёк делаем светлее в компоненте)
  // - доставлено (sent/delivered) — без галочек
  // - прочитано (read) — одна аккуратная галочка
  if (status === 'read') return '✓';
  if (status === 'error') return '⚠️';
  return '';
}

/**
 * Проверяет, является ли сообщение системным.
 */
export function isSystemMessage(message: { type?: string }): boolean {
  return message.type === 'system';
}

/**
 * Группирует сообщения по дате.
 */
export function groupMessagesByDate<T extends { created_at: string }>(
  messages: T[]
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  
  for (const message of messages) {
    const date = new Date(message.created_at).toLocaleDateString('ru-RU');
    const existing = groups.get(date) || [];
    existing.push(message);
    groups.set(date, existing);
  }
  
  return groups;
}

